import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Container, Grid, MenuItem, Paper, Snackbar, TextField, Typography } from '@mui/material';
import PageBanner from '../../components/Common/PageBanner';
import SectionHeading from '../../components/Common/SectionHeading';
import { franchiseBenefits, franchiseSteps, pageContent, productSelectOptions } from '../../data/siteData';
import { submitNetlifyForm } from '../../utils/netlifyForms';

const franchiseFormName = 'franchise-enquiry';

function FranchisePage() {
  const content = pageContent.franchise;
  const navigate = useNavigate();
  const initialForm = useMemo(
    () => ({
      name: '',
      phone: '',
      city: '',
      state: '',
      range: '',
      message: '',
    }),
    []
  );
  const [formData, setFormData] = useState(initialForm);
  const [feedback, setFeedback] = useState({ open: false, severity: 'success', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setIsSubmitting(true);
      await submitNetlifyForm(franchiseFormName, formData);
      setFeedback({ open: true, severity: 'success', message: content.formSection.successMessage });
      setFormData(initialForm);
      setTimeout(() => {
        navigate('/contact');
      }, 900);
    } catch (error) {
      setFeedback({ open: true, severity: 'error', message: content.formSection.errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <PageBanner {...content.banner} />
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <SectionHeading
          eyebrow={content.stepsSection.eyebrow}
          title={content.stepsSection.title}
          subtitle={content.stepsSection.subtitle}
        />
        <Grid container spacing={3} sx={{ mb: 8 }}>
          {franchiseSteps.map((step, index) => (
            <Grid item xs={12} md={4} key={step.title}>
              <Paper elevation={0} sx={{ p: 3, height: '100%' }}>
                <Typography sx={{ color: '#c9a84c', fontWeight: 900, mb: 1 }}>0{index + 1}</Typography>
                <Typography variant="h4" sx={{ mb: 1.2 }}>{step.title}</Typography>
                <Typography sx={{ color: 'text.secondary', lineHeight: 1.75 }}>{step.description}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <SectionHeading
          eyebrow={content.benefitsSection.eyebrow}
          title={content.benefitsSection.title}
          subtitle={content.benefitsSection.subtitle}
        />
        <Grid container spacing={3} sx={{ mb: 8 }}>
          {franchiseBenefits.map((benefit) => {
            const Icon = benefit.icon;
            return (
              <Grid item xs={12} sm={6} md={3} key={benefit.title}>
                <Card elevation={0} sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Icon sx={{ color: '#c9a84c', fontSize: 34 }} />
                    <Typography variant="h4" sx={{ mt: 2, mb: 1.2 }}>{benefit.title}</Typography>
                    <Typography sx={{ color: 'text.secondary', lineHeight: 1.75 }}>{benefit.description}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Paper elevation={0} sx={{ p: { xs: 3, md: 4 } }}>
          <Typography variant="h3" sx={{ mb: 3 }}>{content.formSection.title}</Typography>
          <Box
            component="form"
            name={franchiseFormName}
            method="post"
            action="/"
            data-netlify="true"
            netlify-honeypot="bot-field"
            onSubmit={handleSubmit}
          >
            <input type="hidden" name="form-name" value={franchiseFormName} />
            <input type="hidden" name="bot-field" />
            <Grid container spacing={2}>
              {content.formSection.fields
                .filter((field) => field.name !== 'message')
                .map((field) => (
                  <Grid item xs={12} sm={6} key={field.name}>
                    <TextField
                      fullWidth
                      name={field.name}
                      label={field.label}
                      required={field.required}
                      value={formData[field.name]}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </Grid>
                ))}
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  name="range"
                  label={content.formSection.rangeLabel}
                  value={formData.range}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  {productSelectOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              {content.formSection.fields
                .filter((field) => field.name === 'message')
                .map((field) => (
                  <Grid item xs={12} key={field.name}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={field.rows}
                      name={field.name}
                      label={field.label}
                      required={field.required}
                      value={formData[field.name]}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </Grid>
                ))}
              <Grid item xs={12}>
                <Button type="submit" variant="contained" color="primary" disabled={isSubmitting}>
                  {isSubmitting ? content.formSection.submittingLabel : content.formSection.submitLabel}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>
      </Container>

      <Snackbar
        open={feedback.open}
        autoHideDuration={4000}
        onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={feedback.severity}
          variant="filled"
          onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default FranchisePage;
